import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useDisconnectXeroConnection, useSelectXeroTenant, useXeroConnection } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ConfirmActionDialog } from './ConfirmActionDialog';

type XeroConnectionBodyProps = {
  memberId: number;
  isHubAdmin: boolean;
  onDisconnected: () => void;
};

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString();
}

export function XeroConnectionBody({ memberId, isHubAdmin, onDisconnected }: XeroConnectionBodyProps) {
  const connectionQuery = useXeroConnection(memberId);
  const selectTenant = useSelectXeroTenant();
  const disconnect = useDisconnectXeroConnection();
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const handleSelectTenant = () => {
    if (!selectedTenantId) {
      return;
    }

    selectTenant.mutate(
      { xeroTenantId: selectedTenantId },
      {
        onSuccess: (result) => {
          toast.success(`Connected to ${result.xeroTenantName ?? 'the Xero organisation'}.`);
        },
        onError: (error: unknown) => {
          toast.error(getAxiosErrorDisplayMessage(error, 'Failed to select the Xero organisation.'));
        },
      }
    );
  };

  const handleDisconnect = () => {
    disconnect.mutate(undefined, {
      onSuccess: () => {
        toast.success('Xero disconnected.');
        onDisconnected();
      },
      onError: (error: unknown) => {
        toast.error(getAxiosErrorDisplayMessage(error, 'Failed to disconnect Xero.'));
      },
    });
  };

  const connection = connectionQuery.data?.connection ?? null;
  const candidates = connectionQuery.data?.candidates ?? [];
  const webhookUrl = connectionQuery.data?.webhookUrl ?? null;

  return (
    <>
      {connectionQuery.isLoading ? (
        <div className="rounded-md border p-4 text-sm text-muted-foreground">Loading Xero connection...</div>
      ) : null}

      {connectionQuery.isError ? (
        <div className="rounded-md border p-4 text-sm text-destructive">Failed to load the Xero connection.</div>
      ) : null}

      {connection?.status === 'PENDING_TENANT_SELECTION' ? (
        <div className="rounded-md border p-4 space-y-3">
          <p className="text-sm">
            Your Xero authorisation covers several organisations. Exactly one maps to this Hub —
            choose which:
          </p>
          {isHubAdmin ? (
            <>
              <RadioGroup value={selectedTenantId} onValueChange={setSelectedTenantId}>
                {candidates.map((candidate) => (
                  <div key={candidate.xeroTenantId} className="flex items-center space-x-2">
                    <RadioGroupItem value={candidate.xeroTenantId} id={`xero-tenant-${candidate.xeroTenantId}`} />
                    <Label htmlFor={`xero-tenant-${candidate.xeroTenantId}`}>
                      {candidate.xeroTenantName}
                      <span className="ml-2 text-xs text-muted-foreground">{candidate.xeroTenantType}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <Button
                type="button"
                onClick={handleSelectTenant}
                disabled={!selectedTenantId || selectTenant.isPending}
              >
                {selectTenant.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirm organisation
              </Button>
            </>
          ) : null}
        </div>
      ) : null}

      {connection?.status === 'ACTIVE' ? (
        <div className="rounded-md border p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="font-medium">{connection.xeroTenantName ?? 'Xero organisation'}</span>
            <Badge variant="outline">Connected</Badge>
          </div>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Connected by {connection.connectedByMemberEmail ?? 'unknown'}</p>
            <p>Last verified {formatDateTime(connection.lastVerifiedAt)}</p>
          </div>
          {webhookUrl ? (
            <div className="text-sm space-y-1">
              <p className="font-medium">Webhook endpoint</p>
              <code className="block break-all rounded bg-muted px-2 py-1 text-xs">{webhookUrl}</code>
              <p className="text-muted-foreground">
                Paste this into the Vivport app&apos;s Webhooks settings in the Xero developer portal; events arrive for
                every connected organisation.
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Webhook key not configured</p>
          )}
          {isHubAdmin ? (
            <Button
              type="button"
              variant="destructive"
              onClick={() => setDisconnectOpen(true)}
              disabled={disconnect.isPending}
            >
              {disconnect.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Disconnect
            </Button>
          ) : null}
        </div>
      ) : null}

      {connection?.status === 'REVOKED' ? (
        <div className="rounded-md border p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="font-medium">{connection.xeroTenantName ?? 'Xero organisation'}</span>
            <Badge variant="destructive">Revoked</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {connection.revokedReason ?? 'The connection was revoked.'}
          </p>
        </div>
      ) : null}
      <ConfirmActionDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title="Disconnect Xero"
        description="Disconnect Xero from this Hub? Agents and Neo lose Xero access for this Hub, and the connection slot is released at Xero."
        confirmLabel="Disconnect"
        onConfirm={handleDisconnect}
      />
    </>
  );
}
